'use client'

import PropTypes from 'prop-types'
import { WizardPanel } from '@repo/ui/WizardPanel'
import { WizardFormSection } from '@repo/ui/WizardFormSection'
import { WizardIconTitle } from '@repo/ui/WizardIconTitle'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { useNewMatterStore } from '@repo/store/useNewMatterStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { LargeMatterRestrictionIcon } from '@repo/ui/LargeMatterRestrictionIcon'
import MUIStack from '@mui/material/Stack'
import { UserMultiSelect } from '@repo/ui/UserMultiSelect'
import { GroupMultiSelect } from '@repo/ui/GroupMultiSelect'
import MUICheckbox from '@mui/material/Checkbox'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIAlert from '@mui/material/Alert'
import { useEffect, useState } from 'react'

export const NewMatterControlMatterAccess = ({ updateStepState, wideMode, handlePrevNext, testData }) => {
  const { groupPermissions, userPermissions, updateState, restrictedAccess } = useNewMatterStore()
  const { matterRestrictedAccess, fetchMatterRestrictedAccess } = useLookupsStore()
  const [showRequiredFieldAlert, setShowRequiredFieldAlert] = useState(false)

  const handleClickContinue = () => {
    if (restrictedAccess && !groupPermissions.length && !userPermissions.length) {
      setShowRequiredFieldAlert(true)
      return
    }

    setShowRequiredFieldAlert(false)

    updateStepState({ status: 'completed', statusText: 'Complete' })
    handlePrevNext(1)
  }

  const handleClickBack = () => {
    handlePrevNext(-1)
  }

  useEffect(() => {
    if (matterRestrictedAccess === null && !testData) {
      fetchMatterRestrictedAccess()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matterRestrictedAccess])

  useEffect(() => {
    if (!restrictedAccess) {
      updateState({ key: 'groupPermissions', value: [] })
      updateState({ key: 'userPermissions', value: [] })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedAccess])

  useEffect(() => {
    if (restrictedAccess && showRequiredFieldAlert) {
      const hasUsers = userPermissions.length > 0
      const hasGroups = groupPermissions.length > 0
      setShowRequiredFieldAlert(!hasUsers && !hasGroups)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPermissions, groupPermissions, restrictedAccess])

  const defaultPermissions =
    matterRestrictedAccess === true
      ? 'As per your system settings, the selected Fee Earner and Supervising Fee Earner are the only users who will have access to this matter'
      : 'All users and groups will have access to this matter'

  return (
    <>
      <WizardPanel data-testid="NewMatterControlMatterAccess">
        <WizardFormSection>
          <WizardIconTitle title="Control matter access" icon={LargeMatterRestrictionIcon} sx={{ mb: 8 }} isOptional />

          <MUIAlert sx={{ mt: -4, mb: 4, userSelect: 'none', pointerEvents: 'none', width: '100%' }} severity="warning">
            {restrictedAccess
              ? `The selected users and groups below will have access to this matter`
              : defaultPermissions}
          </MUIAlert>

          {matterRestrictedAccess === false ? (
            <MUIFormControlLabel
              sx={{ ml: -2, mb: 3, mt: -4, width: 'auto', userSelect: 'none' }}
              control={
                <MUICheckbox
                  checked={restrictedAccess}
                  onChange={(e) => updateState({ key: 'restrictedAccess', value: e.target.checked })}
                />
              }
              label="Apply access restrictions to this matter"
            />
          ) : null}

          {matterRestrictedAccess === false && restrictedAccess ? (
            <MUIStack sx={{ width: '100%' }} gap={6}>
              {showRequiredFieldAlert ? (
                <MUIAlert sx={{ mt: -2, userSelect: 'none', pointerEvents: 'none', width: '100%' }} severity="error">
                  {'Please select at least one user or group to continue, or alternatively remove access restrictions'}
                </MUIAlert>
              ) : null}

              <MUIStack sx={{ width: '100%' }} flexWrap="wrap" direction="row" justifyContent="space-between" gap={2}>
                <UserMultiSelect
                  onChange={(value) => updateState({ key: 'userPermissions', value })}
                  value={userPermissions}
                  size="medium"
                  testData={testData}
                  sx={{ flex: 1, maxWidth: '470px' }}
                />

                <GroupMultiSelect
                  onChange={(value) => updateState({ key: 'groupPermissions', value })}
                  value={groupPermissions}
                  size="medium"
                  testData={testData}
                  sx={{ flex: 1, maxWidth: '470px' }}
                />
              </MUIStack>
            </MUIStack>
          ) : null}
        </WizardFormSection>
      </WizardPanel>
      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleClickContinue}
        handleClickBack={handleClickBack}
        continueButtonDisabled={showRequiredFieldAlert}
      />
    </>
  )
}

NewMatterControlMatterAccess.propTypes = {
  updateStepState: PropTypes.func,
  currentStep: PropTypes.object,
  wideMode: PropTypes.bool,
  handlePrevNext: PropTypes.func,
  testData: PropTypes.array
}
